import { useEffect } from "react";
import {
  getRemoteConfigValue,
  getRemoteConfigValueSource,
  remoteConfigReady,
  getQaGroupOverride,
  QA_GROUP_STORAGE_KEY,
  setRemoteConfigCustomSignals,
  isProduction,
  logEvent,
  logUserProperty,
  logException,
  useAuthStore,
  waitForState,
} from "@um/ui";
import type { Account, WithRef } from "@um/contracts";
import { usePaymentsStore } from "@/store/usePaymentsStore";
import { loadRevenueCatPlatform } from "./revenueCat";

// ─── Routine-menu experiment assignment stamping ────────────────────────────
//
// Firebase A/B can only join exposure with goals inside one app registration,
// and RevenueCat (the who-pays ground truth) can't join with Firebase at all.
// So the variant each user actually received is stamped into three sinks that
// each answer a different question:
//   1. GA user property + `um_experiment_assigned` event — funnels and the
//      per-arm denominator (count UNIQUE users, not events).
//   2. Firestore `Settings/account.routineMenuVariant` — queryable roster of
//      arm membership (CLI pulls, debugging).
//   3. RevenueCat subscriber attribute — trial/paid charts segmented by arm.
//
// Modeled on the paywall experiment stamping (`paywall.ts`
// prefetchRoutinePaywallExperiment) and the FeedbackStrategy observability
// hook (`feedback.ts` useFeedbackStrategyObservability).
//
// Known scope limits (accepted, mirrors the paywall precedent): assignment is
// per Firebase installation (≈ per user/device) while Firestore + RevenueCat
// sinks are per ACCOUNT — two users sharing one account can overwrite each
// other's account-level stamp. In onboarding practice user:account is 1:1.

/** Experiment label attached to `um_experiment_assigned` events. */
const ROUTINE_MENU_EXPERIMENT = "routine_menu";

/** Remote Config parameter that carries the assignment. */
const ROUTINE_MENU_RC_KEY = "RoutineMenuVariant" as const;

/** Marks that the local QA override was mirrored from `Account.attributes.qa_group`. */
const QA_GROUP_FROM_ACCOUNT_KEY = "um.qaGroupFromAccount";

/** Persisted dedupe of stamped assignments, so `um_experiment_assigned` fires
 * once per device per `${userId}:${accountId}:${key}:${value}` — not once per
 * launch. A changed value still re-stamps so crossovers stay visible. */
const STAMPED_STORAGE_KEY = "um.experimentAssignments";

/** In-memory guard for the current session (also covers localStorage failure). */
const sessionStamped = new Set<string>();

function readPersistedStamps(): string[] {
  try {
    const raw = window.localStorage.getItem(STAMPED_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((k) => typeof k === "string") : [];
  } catch {
    return [];
  }
}

function hasStamped(key: string): boolean {
  return sessionStamped.has(key) || readPersistedStamps().includes(key);
}

function markStamped(key: string): void {
  sessionStamped.add(key);
  try {
    const stamps = readPersistedStamps();
    if (!stamps.includes(key)) {
      // Bounded: one entry per (user, account, value) on this device.
      window.localStorage.setItem(
        STAMPED_STORAGE_KEY,
        JSON.stringify([...stamps, key].slice(-20))
      );
    }
  } catch {
    // In-memory guard still prevents same-session duplicates.
  }
}

/** Drops the persisted mark (keeps the in-session one) so the next launch
 * retries all sinks when one of them failed. */
function unpersistStamp(key: string): void {
  try {
    window.localStorage.setItem(
      STAMPED_STORAGE_KEY,
      JSON.stringify(readPersistedStamps().filter((k) => k !== key))
    );
  } catch {
    // Next launch will retry anyway if persistence is unavailable.
  }
}

export { getQaGroupOverride };

/**
 * Sets (or clears, with `null`) the QA variant override.
 *
 * Forcing is served by Remote Config template conditions, so it takes effect
 * on the NEXT launch's fetch:
 * - web SDK builds: the override is sent as the `qa_group` custom signal
 *   (persisted across sessions by the SDK — cleared here with `null`).
 * - native SDK builds: custom signals don't exist; the `qa_group` GA user
 *   property drives `app.userProperty['qa_group']` conditions instead
 *   (subject to GA propagation lag).
 *
 * Devices with an active override are excluded from assignment stamping.
 */
export function setQaGroupOverride(value: "juicy" | "control" | null): void {
  try {
    if (value) {
      window.localStorage.setItem(QA_GROUP_STORAGE_KEY, value);
    } else {
      window.localStorage.removeItem(QA_GROUP_STORAGE_KEY);
    }
    // Manual action owns the override from now on — a server-side mirror
    // should neither re-clear nor fight it.
    window.localStorage.removeItem(QA_GROUP_FROM_ACCOUNT_KEY);
  } catch (e) {
    console.warn("[experiments] failed to persist QA override", e);
  }
  // Keep every targeting surface in sync; `null` deletes the custom signal so
  // a cleared device stops matching qa_force_* conditions on future fetches.
  void setRemoteConfigCustomSignals({ qa_group: value ?? null });
  logUserProperty("qa_group", value ?? "");
  console.log(
    `[experiments] QA override ${value ? `set to "${value}"` : "cleared"} — relaunch to apply`
  );
}

/**
 * Mirrors server-set `Account.attributes.qa_group` (set via `um account` for
 * any tester) into the local override, and releases the device back to
 * organic assignment when the server attribute is removed — but only if the
 * local override came from the mirror (a manually set override wins).
 * Effective on the next launch, same as the local override.
 */
function applyMirroredQaGroup(remoteQaGroup: string | undefined): void {
  const remote = remoteQaGroup?.trim() || null;
  const local = getQaGroupOverride();
  let mirroredFromAccount = false;
  try {
    mirroredFromAccount =
      window.localStorage.getItem(QA_GROUP_FROM_ACCOUNT_KEY) === "1";
  } catch {
    return;
  }

  if (remote) {
    if (local === remote) return;
    try {
      window.localStorage.setItem(QA_GROUP_STORAGE_KEY, remote);
      window.localStorage.setItem(QA_GROUP_FROM_ACCOUNT_KEY, "1");
    } catch (e) {
      console.warn("[experiments] failed to mirror remote qa_group", e);
      return;
    }
    void setRemoteConfigCustomSignals({ qa_group: remote });
    logUserProperty("qa_group", remote);
    console.log(
      `[experiments] mirrored Account.attributes.qa_group="${remote}" to local override — effective next launch`
    );
  } else if (local && mirroredFromAccount) {
    try {
      window.localStorage.removeItem(QA_GROUP_STORAGE_KEY);
      window.localStorage.removeItem(QA_GROUP_FROM_ACCOUNT_KEY);
    } catch {
      return;
    }
    void setRemoteConfigCustomSignals({ qa_group: null });
    logUserProperty("qa_group", "");
    console.log(
      "[experiments] Account.attributes.qa_group removed — cleared mirrored override, organic assignment resumes next launch"
    );
  }
}

/**
 * Records the current routine-menu variant into all three sinks.
 *
 * Stamps only when the value came from the Remote Config server
 * (`source === "remote"`) — users whose fetch failed see the default but are
 * deliberately NOT stamped, so they fall out of both arms instead of
 * polluting control. QA-forced devices are skipped entirely. A DEV env
 * override stamps too (for exercising this flow locally) but never against
 * the production project.
 */
export async function recordExperimentAssignment(
  userId: string,
  accountId: string
): Promise<void> {
  await remoteConfigReady;
  if (!canStampAccount(userId, accountId)) return;

  const qaOverride = getQaGroupOverride();
  if (qaOverride) {
    console.warn(
      `[experiments] QA override active ("${qaOverride}") — skipping assignment stamping`
    );
    return;
  }

  const value = (getRemoteConfigValue(ROUTINE_MENU_RC_KEY) as string) || "";
  const source = getRemoteConfigValueSource(ROUTINE_MENU_RC_KEY);

  if (!value) {
    console.log("[experiments] no RoutineMenuVariant value — not stamping");
    return;
  }
  const isDevOverride = source === "env_override" && !isProduction();
  if (source !== "remote" && !isDevOverride) {
    console.log(
      `[experiments] RoutineMenuVariant source=${source} — not stamping (only remote assignments count)`
    );
    return;
  }

  const dedupeKey = `${userId}:${accountId}:${ROUTINE_MENU_RC_KEY}:${value}`;
  if (hasStamped(dedupeKey)) return;
  markStamped(dedupeKey);

  console.log(
    `[experiments] stamping assignment — ${ROUTINE_MENU_RC_KEY}=${value} source=${source} user=${userId} account=${accountId}`
  );

  // Sink 1: GA — user property for slicing + assignment event (denominator:
  // count UNIQUE users per arm; routes to native GA on device, same stream
  // as the goal events).
  logUserProperty(ROUTINE_MENU_RC_KEY, value);
  logEvent("um_experiment_assigned", {
    experiment: ROUTINE_MENU_EXPERIMENT,
    variant: value,
    source,
  });

  let sinkFailed = false;

  await Promise.all([
    // Sink 2: Firestore Settings/account.
    useAuthStore
      .getState()
      .updateAccountSettings({ routineMenuVariant: value })
      .catch((e) => {
        sinkFailed = true;
        console.error("[experiments] failed to write Firestore stamp", e);
        logException(e);
      }),
    // Sink 3: RevenueCat subscriber attribute (after the SDK session is
    // ready — bounded wait so a payments failure can't hang this forever).
    (async () => {
      const ready = await Promise.race([
        waitForState(usePaymentsStore, (s) => s.isReady).then(() => true),
        new Promise<boolean>((resolve) =>
          setTimeout(() => resolve(false), 30_000)
        ),
      ]);
      if (!ready) {
        throw new Error("payments not ready within 30s");
      }
      // Shared device guard: don't late-write onto a different account's
      // RevenueCat subscriber.
      if (!canStampAccount(userId, accountId)) {
        throw new Error("account changed before RevenueCat stamp");
      }
      const platform = await loadRevenueCatPlatform();
      if (!canStampAccount(userId, accountId)) {
        throw new Error("account changed while loading RevenueCat");
      }
      await platform.setSubscriberAttributes({
        routineMenuVariant: value,
      });
    })().catch((e) => {
      sinkFailed = true;
      console.warn("[experiments] failed to write RevenueCat stamp", e);
      logException(e);
    }),
  ]);

  if (sinkFailed) {
    // Retry every sink on the next launch (idempotent writes; the GA event
    // may repeat for this rare failure case — count unique users, not events).
    unpersistStamp(dedupeKey);
  }
}

function canStampAccount(userId: string, accountId: string): boolean {
  const state = useAuthStore.getState();
  return (
    state.user?.uid === userId &&
    state.accountId === accountId &&
    !state.isHandlingCredentialCollision &&
    state.account?.ref.id === accountId &&
    !!state.account.users?.some((member) => member.id === userId)
  );
}

/**
 * Mounts once (App.tsx AppWrapper) and stamps the routine-menu assignment as
 * soon as both the user and account are known — lands ~1-2s after boot, ahead
 * of `um_create_character`. Safe on every route including `/onboarding`.
 * Also keeps the server-driven QA-forcing mirror in sync with the account doc
 * (which loads asynchronously after the ids are available).
 */
export function useExperimentAssignmentStamping(
  userId: string | null | undefined,
  accountId: string | null | undefined
): void {
  const accountReady = useAuthStore(
    (state) =>
      !!userId &&
      !!accountId &&
      !state.isHandlingCredentialCollision &&
      state.user?.uid === userId &&
      state.account?.ref.id === accountId &&
      !!state.account.users?.some((member) => member.id === userId)
  );
  useEffect(() => {
    if (!userId || !accountId || !accountReady) return;

    // Mirror runs whenever the account doc (re)loads — only against a loaded
    // doc for the CURRENT account, so a not-yet-loaded account can't be
    // mistaken for "attribute removed".
    const syncMirror = (account: WithRef<Account> | null | undefined) => {
      if (!account?.ref || account.ref.id !== accountId) return;
      applyMirroredQaGroup(account.attributes?.qa_group);
    };
    syncMirror(useAuthStore.getState().account);
    const unsubscribe = useAuthStore.subscribe((state) =>
      syncMirror(state.account)
    );

    recordExperimentAssignment(userId, accountId).catch((e) => {
      console.error("[experiments] assignment stamping failed", e);
      logException(e);
    });

    return unsubscribe;
  }, [userId, accountId, accountReady]);
}
